import {StockLevelOverwrite} from '@domain/material-storage'
import {StockDevelopmentData} from '@domain/schedule'
import {IncomingMaterial, IncomingMaterialIndicator} from '@features/storage-levels'
import {useResizableChartWidth} from '@widgets/common-d3-chart'
import {
  StockDevelopmentChartConfigProvider,
  StockDevelopmentChartContent,
  useStockDevelopmentChartConfigData
} from '@widgets/stock-card'
import * as d3 from 'd3'
import type {Moment} from 'moment-timezone'
import React, {useCallback} from 'react'

interface PlanningStockDevelopmentChartProps {
  timezoneId: string
  listOfDays: Moment[]
  stockData: StockDevelopmentData & {
    stockOverwrites?: StockLevelOverwrite[]
  }
  height: number
  minTargetLevels?: {weekend: number; week: number}
  deadStockLevel?: number
  storageCapacity?: number
  incomingMaterialHourly?: Record<string, IncomingMaterial[]>
}

export const PlanningStockDevelopmentChart: React.FC<PlanningStockDevelopmentChartProps> = ({
  timezoneId,
  listOfDays,
  stockData,
  minTargetLevels,
  deadStockLevel,
  storageCapacity,
  incomingMaterialHourly,
  height
}) => {
  const [chartRef, width] = useResizableChartWidth()

  const stockDevelopmentData = useStockDevelopmentChartConfigData({
    stockData,
    listOfDays,
    timezoneId,
    minTargetLevels,
    storageCapacity,
    height,
    width
  })

  const renderIndicators = useCallback(
    ({
      xScale,
      yScale,
      stockLevelHourly
    }: {
      xScale: d3.ScaleTime<number, number, never>
      yScale: d3.ScaleLinear<number, number, never>
      stockLevelHourly: Record<string, number>
    }) => {
      if (!incomingMaterialHourly) return null

      return Object.entries(incomingMaterialHourly).flatMap(([datetime, materials]) =>
        materials.map((material, index) => {
          const x = xScale(material.datetime.toDate())
          const stockLevel = stockLevelHourly[material.datetime.toISOString()] ?? 0
          const cumulativeValue = materials.slice(0, index).reduce((acc, m) => acc + m.value, 0)
          const y = yScale(stockLevel + cumulativeValue)
          return (
            <IncomingMaterialIndicator
              key={`${datetime}-${index}`}
              x={x}
              y={y}
              incomingMaterial={material}
            />
          )
        })
      )
    },
    [incomingMaterialHourly]
  )

  return (
    <StockDevelopmentChartConfigProvider {...stockDevelopmentData}>
      <StockDevelopmentChartContent
        listOfDays={listOfDays}
        stockData={stockData}
        minTargetLevels={minTargetLevels}
        deadStockLevel={deadStockLevel}
        storageCapacity={storageCapacity}
        height={height}
        width={width}
        chartRef={chartRef}
        renderIndicators={incomingMaterialHourly ? renderIndicators : undefined}
      />
    </StockDevelopmentChartConfigProvider>
  )
}
